// Central catalog of every permission the OpinionAdmin app knows about.
// Grouped so the role-management UI can render a tidy matrix.
// A permission KEY is the stable identifier stored in the DB; LABEL is UI only.

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  SALES: "sales",
};

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: "Super Admin",
  [ROLES.ADMIN]: "Admin",
  [ROLES.SALES]: "Sales",
};

export const PERMISSION_GROUPS = [
  {
    key: "dashboard",
    label: "Dashboard",
    permissions: [
      { key: "dashboard.view", label: "View Dashboard" },
    ],
  },
  {
    key: "user_management",
    label: "User Management",
    permissions: [
      { key: "users.view", label: "View User List" },
      { key: "users.create", label: "Create User" },
      { key: "users.edit", label: "Edit User" },
      { key: "users.delete", label: "Delete User" },
      { key: "users.wallet_history", label: "View User Wallet History" },
      { key: "users.change_password", label: "Change User Password" },
      { key: "demo_users.view", label: "View Demo User List" },
      { key: "user_360.view", label: "User 360 Report" },
      { key: "user_bets.view", label: "User Bets Report" },
    ],
  },
  {
    key: "finance",
    label: "Finance",
    permissions: [
      { key: "user_points.view", label: "View User Points" },
      { key: "user_points.manage", label: "Manage User Points / Wallet" },
      { key: "withdrawals.view", label: "View Withdrawals" },
      { key: "withdrawals.approve", label: "Approve Withdrawals" },
      { key: "withdrawals.reject", label: "Reject Withdrawals" },
      { key: "bonus.view", label: "View Bonus Management" },
      { key: "bonus.manage", label: "Manage Bonuses" },
      { key: "upi.view", label: "View UPI Settings" },
      { key: "upi.manage", label: "Manage UPI Settings" },
      { key: "payments.view", label: "View Deposit / Payments" },
      { key: "payments.approve", label: "Approve Deposits" },
    ],
  },
  {
    key: "sections_overview",
    label: "Sections (Overview)",
    permissions: [
      { key: "sections.add_all", label: "Add All Sections" },
      { key: "section_a.list", label: "Section A List" },
      { key: "section_b.list", label: "Section B List" },
      { key: "section_c.list", label: "Section C List" },
    ],
  },
  {
    key: "section_a",
    label: "Section A",
    permissions: [
      { key: "section_a.questions", label: "A Section Questions" },
      { key: "section_a.match_result", label: "Match Result" },
      { key: "section_a.result_add", label: "Add Question Result" },
      { key: "section_a.result_list", label: "Result List" },
    ],
  },
  {
    key: "section_b",
    label: "Section B",
    permissions: [
      { key: "section_b.points_add", label: "Add Games Points" },
      { key: "section_b.points_list", label: "Games Points List" },
      { key: "section_b.result_add", label: "Add Points Result" },
      { key: "section_b.result_list", label: "Points Result List" },
    ],
  },
  {
    key: "section_c",
    label: "Section C",
    permissions: [
      { key: "section_c.question_add", label: "Add Question Game" },
      { key: "section_c.question_list", label: "Question Game List" },
      { key: "section_c.result_add", label: "Add Game Result" },
      { key: "section_c.result_list", label: "Game Result List" },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    permissions: [
      { key: "reports.all", label: "All Reports" },
      { key: "reports.all_user", label: "All User Reports" },
      { key: "reports.match_bets", label: "Match Bets Report" },
      { key: "reports.user_bets", label: "User Bets Report" },
      { key: "reports.section_a", label: "Section A Reports" },
      { key: "reports.section_b", label: "Section B Reports" },
      { key: "reports.section_c", label: "Section C Reports" },
    ],
  },
  {
    key: "notifications",
    label: "Notifications",
    permissions: [
      { key: "notifications.view", label: "View Notifications" },
      { key: "notifications.manage", label: "Send / Manage Notifications" },
    ],
  },
  {
    key: "media_settings",
    label: "Media & Settings",
    permissions: [
      { key: "ads.view", label: "View Ads / Banners" },
      { key: "ads.manage", label: "Add / Edit Ads" },
      { key: "packages.view", label: "View Packages" },
      { key: "packages.manage", label: "Manage Packages" },
    ],
  },
  {
    key: "game_setup",
    label: "Game Setup",
    permissions: [
      { key: "leagues.manage", label: "League Management" },
      { key: "teams.manage", label: "Team Management" },
      { key: "matches.manage", label: "Match Management" },
    ],
  },
  {
    key: "admin_access",
    label: "Admin & Roles",
    permissions: [
      { key: "admin_users.view", label: "View Admin Users" },
      { key: "admin_users.manage", label: "Create / Edit / Delete Admin Users" },
      { key: "roles.view", label: "View Role Management" },
      { key: "roles.manage_permissions", label: "Edit Role Permissions" },
      { key: "roles.manage", label: "Create / Rename / Delete Roles" },
    ],
  },
];

// Flat list of all known permission keys.
export const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.key)
);

// Default seed permissions for non-super-admin roles (used only if the
// backend has not yet seeded the role). Super admin always has everything.
export const DEFAULT_ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    "dashboard.view",
    "users.view", "users.create", "users.edit", "users.wallet_history", "users.change_password",
    "demo_users.view", "user_360.view", "user_bets.view",
    "user_points.view", "user_points.manage",
    "withdrawals.view", "withdrawals.approve", "withdrawals.reject",
    "bonus.view", "bonus.manage",
    "upi.view", "upi.manage",
    "payments.view", "payments.approve",
    "sections.add_all",
    "section_a.list", "section_b.list", "section_c.list",
    "section_a.questions", "section_a.match_result", "section_a.result_add", "section_a.result_list",
    "section_b.points_add", "section_b.points_list", "section_b.result_add", "section_b.result_list",
    "section_c.question_add", "section_c.question_list", "section_c.result_add", "section_c.result_list",
    "reports.all", "reports.all_user", "reports.match_bets", "reports.user_bets",
    "reports.section_a", "reports.section_b", "reports.section_c",
    "notifications.view", "notifications.manage",
    "ads.view", "ads.manage",
    "packages.view", "packages.manage",
    "leagues.manage", "teams.manage", "matches.manage",
  ],
  [ROLES.SALES]: [
    "dashboard.view",
    "users.view", "users.create",
    "demo_users.view", "user_360.view",
    "user_points.view",
    "payments.view",
    "reports.all_user",
  ],
};

// hasPermission — super_admin always passes, everyone else checks the list.
export const hasPermission = (user, permissions, key) => {
  if (!user) return false;
  if (user.role === ROLES.SUPER_ADMIN) return true;
  if (!key) return true;
  return Array.isArray(permissions) && permissions.includes(key);
};

// hasAny — ORs a list of permission keys.
export const hasAny = (user, permissions, keys = []) => {
  if (!user) return false;
  if (user.role === ROLES.SUPER_ADMIN) return true;
  if (!Array.isArray(keys) || keys.length === 0) return true;
  return keys.some((k) => permissions.includes(k));
};
